import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useUser, useClerk } from "@clerk/clerk-react";
import { COLORS } from "../hooks/useAudioRecorder";

/**
 * Small auth-state indicator. Anonymous: a "Sign in" mono link that
 * preserves the current URL via redirect_url. Signed-in: a 34px circle
 * avatar showing the user's first initial, with a tap-to-open menu
 * containing the full email + Sign out button.
 *
 * The avatar matches the History/Settings circle buttons on Home (1px
 * rule border, transparent bg, ink-dim foreground). Renders nothing while
 * Clerk is still loading to avoid a layout flash.
 */
export default function AuthChip() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return (
      <Link
        to={`/sign-in?redirect_url=${redirect}`}
        data-testid="auth-chip-signin"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: COLORS.yellow,
          textDecoration: "none",
        }}
      >
        Sign in
      </Link>
    );
  }

  const email = user?.primaryEmailAddress?.emailAddress || "";
  const initial = (
    user?.firstName?.[0] ||
    user?.lastName?.[0] ||
    email?.[0] ||
    "?"
  ).toUpperCase();

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        data-testid="auth-chip-email"
        aria-label="Account"
        onClick={() => setOpen((v) => !v)}
        // setTimeout so an inner button click registers before the menu closes.
        onBlur={() => setTimeout(() => setOpen(false), 140)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 999,
          border: `1px solid ${COLORS.rule}`,
          backgroundColor: "transparent",
          color: "#a8a292",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "0.04em",
          padding: 0,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 160ms ease, border-color 160ms ease",
        }}
      >
        {initial}
      </button>
      {open && (
        <div
          data-testid="auth-chip-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 30,
            minWidth: 220,
            padding: "10px 12px",
            borderRadius: 10,
            backgroundColor: "#1a1917",
            border: `1px solid ${COLORS.rule}`,
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 10,
              color: COLORS.faint,
              letterSpacing: "0.04em",
              wordBreak: "break-all",
              lineHeight: 1.4,
            }}
          >
            {email}
          </div>
          <button
            type="button"
            data-testid="auth-chip-signout"
            // onMouseDown + preventDefault so the parent button's onBlur
            // doesn't fire and close the menu before the click registers.
            onMouseDown={(e) => {
              e.preventDefault();
              signOut();
            }}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: `1px solid ${COLORS.rule}`,
              background: "transparent",
              color: COLORS.text,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </span>
  );
}
